import { Injectable, signal, computed } from '@angular/core';
import {
  LiveSource,
  LiveSourceStatus,
  LiveSourceStatusFilter,
  LiveSourceProtocolFilter,
} from '../../domain/live-sources';
import { EStreamSourceType } from '../../domain/live-feed';

/**
 * Signal-based state store for the Live Sources module.
 * Single source of truth — all live sources data flows through this service.
 */
@Injectable({ providedIn: 'root' })
export class LiveSourcesStateService {
  // ── Private writable signals ──
  private readonly sourcesSignal = signal<LiveSource[]>([]);
  private readonly searchQuerySignal = signal<string>('');
  private readonly statusFilterSignal = signal<LiveSourceStatusFilter>('All');
  private readonly protocolFilterSignal = signal<LiveSourceProtocolFilter>('All');
  private readonly isLoadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);

  // ── Public readonly signals ──
  readonly sources = this.sourcesSignal.asReadonly();
  readonly searchQuery = this.searchQuerySignal.asReadonly();
  readonly statusFilter = this.statusFilterSignal.asReadonly();
  readonly protocolFilter = this.protocolFilterSignal.asReadonly();
  readonly isLoading = this.isLoadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  // ── Computed signals ──

  /** Combines sources, searchQuery, statusFilter, and protocolFilter. Case-insensitive search on name. */
  readonly filteredSources = computed(() => {
    let result = this.sourcesSignal();
    const query = this.searchQuerySignal().toLowerCase().trim();
    const status = this.statusFilterSignal();
    const protocol = this.protocolFilterSignal();

    if (query) {
      result = result.filter((s) => s.name.toLowerCase().includes(query));
    }
    if (status !== 'All') {
      result = result.filter((s) => s.status === status);
    }
    if (protocol !== 'All') {
      result = result.filter((s) => s.sourceType === protocol);
    }
    return result;
  });

  /** Total count of all sources (unfiltered). */
  readonly totalCount = computed(() => this.sourcesSignal().length);

  /** Count of active sources. */
  readonly activeCount = computed(
    () => this.sourcesSignal().filter((s) => s.status === LiveSourceStatus.Active).length,
  );

  /** Count of error sources. */
  readonly errorCount = computed(
    () => this.sourcesSignal().filter((s) => s.status === LiveSourceStatus.Error).length,
  );

  // ── Setters (called by container component) ──

  /** Set all live sources. */
  setSources(sources: LiveSource[]): void {
    this.sourcesSignal.set(sources);
  }

  /** Set search query text. */
  setSearchQuery(query: string): void {
    this.searchQuerySignal.set(query);
  }

  /** Set status filter. */
  setStatusFilter(status: LiveSourceStatusFilter): void {
    this.statusFilterSignal.set(status);
  }

  /** Set protocol filter. */
  setProtocolFilter(protocol: LiveSourceProtocolFilter): void {
    this.protocolFilterSignal.set(protocol);
  }

  /** Toggle loading state. */
  setLoading(loading: boolean): void {
    this.isLoadingSignal.set(loading);
  }

  /** Set or clear error message. */
  setError(error: string | null): void {
    this.errorSignal.set(error);
  }

  /** Reset all state to defaults. */
  reset(): void {
    this.sourcesSignal.set([]);
    this.searchQuerySignal.set('');
    this.statusFilterSignal.set('All');
    this.protocolFilterSignal.set('All');
    this.isLoadingSignal.set(false);
    this.errorSignal.set(null);
  }
}
